import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import type { ScLinkField } from '@core/jss-models';
import { TrackToolService } from '@analytics/events';

import {
  answerMappingError,
  leaveQuestionnaire,
  loadNextQuestion,
  submitQuestionnaireAnswer,
} from './homebattery-questionnaire.actions';
import { selectHomebatteryQuestionnaireStructure } from './homebattery-questionnaire.selectors';
import { onHomeBatteryFunnelNavigationAction } from '../actions';
import { selectHomeBatteryFunnelSettings } from '../selectors';
import { parsedHomebatteryFunnelToolTrackingConfig } from './homebattery-questionnaire.analytics-config';

@Injectable()
export class HomebatteryQuestionnaireEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);

  handleAnswerSubmit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submitQuestionnaireAnswer),
      concatLatestFrom((_) => [
        this.store.select(selectHomebatteryQuestionnaireStructure),
        this.store.select(selectHomeBatteryFunnelSettings),
      ]),
      mergeMap(([action, structure, funnelSettings]) => {
        const target = action.answer.target;
        switch (target.type) {
          case 'next-question': {
            const next = target.questionName;
            return next in structure
              ? [
                  loadNextQuestion({
                    question: structure[next],
                  }),
                ]
              : [answerMappingError()];
          }
          case 'link': {
            const funnelKey = target.funnelKey as keyof typeof funnelSettings;
            const next = funnelSettings[funnelKey] as ScLinkField;
            return [
              onHomeBatteryFunnelNavigationAction({ page: next }),
              leaveQuestionnaire(),
            ];
          }
          default:
            return [answerMappingError()];
        }
      })
    );
  });

  handleLeaveQuestionnaire$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(leaveQuestionnaire),
        tap(() => {
          /**
           * WTA-17: resolve injector context
           */
          const trackToolService = this.injector.get(TrackToolService);
          trackToolService.completeTool(
            parsedHomebatteryFunnelToolTrackingConfig
          );
        })
      ),
    { dispatch: false }
  );
}
